// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {
	ANIMATED_AVATAR_FORMATS,
	ANIMATED_IMAGE_FORMATS,
	AVATAR_RECOMMENDED_SIZE_LABEL,
	FLUXER_TAG_DIGIT_COUNT,
	IMAGE_MAX_SIZE_LABEL,
	PREMIUM_PRODUCT_NAME,
	STATIC_IMAGE_FORMATS,
} from '@app/features/app/config/I18nDisplayConstants';
import {LimitResolver} from '@app/features/app/utils/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/features/app/utils/LimitUtils';
import {AssetCropModal, AssetType} from '@app/features/expressions/components/modals/AssetCropModal';
import {showAnimatedAvifConfirmModal} from '@app/features/expressions/utils/AnimatedAvifModalUtils';
import {
	getAnimatedFormatLabel,
	isAnimatedFile,
	shouldShowAnimatedAvifConfirmation,
} from '@app/features/expressions/utils/AnimatedImageUtils';
import {getAcceptString, getAssetFormatErrorMessage} from '@app/features/expressions/utils/AssetFormatCopy';
import {isGif} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/utils/ImageAsset';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {shouldShowPremiumFeatures} from '@app/features/premium/utils/PremiumUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {PlutoniumLink} from '@app/features/ui/plutonium_link/PlutoniumLink';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/AvatarUploader.module.css';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {canCropFormat} from '@app/features/voice/utils/MediaCapabilities';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback} from 'react';
export type AvatarMode = 'inherit' | 'custom' | 'unset';
interface AvatarUploaderProps {
	hasAvatar: boolean;
	onAvatarChange: (base64: string) => void;
	onAvatarClear: () => void;
	disabled?: boolean;
	disableModeSelection?: boolean;
	isPerGuildProfile: boolean;
	errorMessage?: string;
	avatarMode?: AvatarMode;
	onAvatarModeChange?: (mode: AvatarMode) => void;
}
export const AvatarUploader = observer(
	({
		hasAvatar,
		onAvatarChange,
		onAvatarClear,
		disabled,
		disableModeSelection,
		isPerGuildProfile,
		errorMessage,
		avatarMode = 'inherit',
		onAvatarModeChange,
	}: AvatarUploaderProps) => {
		const {t, i18n} = useLingui();
		const hasAnimatedAvatar = isLimitToggleEnabled(
			{feature_animated_avatar: LimitResolver.resolve({key: 'feature_animated_avatar', fallback: 0})},
			'feature_animated_avatar',
		);
		const getAvatarModeOptions = useCallback(
			() => [
				{
					value: 'inherit' as AvatarMode,
					name: t`Use Global Profile`,
					desc: t`Show your global profile avatar in this community`,
				},
				{
					value: 'custom' as AvatarMode,
					name: t`Use Custom Image`,
					desc: t`Upload a custom avatar for this community`,
					disabled: !hasAnimatedAvatar,
				},
				{
					value: 'unset' as AvatarMode,
					name: t`Don't Show`,
					desc: t`Show default avatar, ignoring your global profile`,
				},
			],
			[t, hasAnimatedAvatar],
		);
		const handleAvatarUpload = useCallback(async () => {
			try {
				const [file] = await openFilePicker({accept: getAcceptString('avatar')});
				if (!file) return;
				if (file.size > 10 * 1024 * 1024) {
					ToastCommands.createToast({
						type: 'error',
						children: t`Avatar file is too large. Please choose a file smaller than ${IMAGE_MAX_SIZE_LABEL}.`,
					});
					return;
				}
				if (!(await canCropFormat(file.type))) {
					ToastCommands.createToast({
						type: 'error',
						children: getAssetFormatErrorMessage(i18n, 'avatar', 'unsupported_mime'),
					});
					return;
				}
				const animated = await isAnimatedFile(file);
				const isGifFile = isGif(file);
				if (animated && !hasAnimatedAvatar) {
					if (shouldShowPremiumFeatures()) {
						ModalCommands.push(
							modal(() => (
								<ConfirmModal
									title={t`Animated avatars require ${PREMIUM_PRODUCT_NAME}`}
									description={
										<>
											<p>
												<Trans>
													Animated avatars ({ANIMATED_AVATAR_FORMATS}) are a premium feature exclusively available to{' '}
													{PREMIUM_PRODUCT_NAME} subscribers.
												</Trans>
											</p>
											<p className={styles.spacedParagraph}>
												<Trans>
													With {PREMIUM_PRODUCT_NAME}, you can use animated avatars ({ANIMATED_AVATAR_FORMATS}), profile
													banners, customize your {FLUXER_TAG_DIGIT_COUNT}-digit tag, and unlock many other premium
													features.
												</Trans>
											</p>
										</>
									}
									primaryText={t`Get ${PREMIUM_PRODUCT_NAME}`}
									primaryVariant="primary"
									secondaryText={t`Cancel`}
									onPrimary={() => {
										window.setTimeout(() => {
											PremiumModalCommands.open();
										}, 0);
									}}
								/>
							)),
						);
					} else {
						ModalCommands.push(
							modal(() => (
								<ConfirmModal
									title={t`Animated avatars not available`}
									description={
										<Trans>
											Animated avatars ({ANIMATED_AVATAR_FORMATS}) are not available on this instance. Please upload a
											static image instead.
										</Trans>
									}
									primaryText={t`Understood`}
									onPrimary={() => {}}
								/>
							)),
						);
					}
					return;
				}
				const base64 = await AvatarUtils.fileToBase64(file);
				const animatedHandled = shouldShowAnimatedAvifConfirmation({
					file,
					isGif: isGifFile,
					animated,
					onAnimatedAvif: () => {
						showAnimatedAvifConfirmModal({
							onConfirm: () => onAvatarChange(base64),
							i18n,
						});
					},
					onOtherAnimated: () => {
						const formatLabel = getAnimatedFormatLabel(file);
						ToastCommands.createToast({
							type: 'info',
							children: t`Cropping animated ${formatLabel} files isn't supported yet. The original upload will be used.`,
						});
						onAvatarChange(base64);
					},
				});
				if (animatedHandled) {
					return;
				}
				ModalCommands.push(
					modal(() => (
						<AssetCropModal
							assetType={AssetType.AVATAR}
							imageUrl={base64}
							sourceMimeType={file.type}
							onCropComplete={(croppedBlob) => {
								const reader = new FileReader();
								reader.onload = () => {
									const croppedBase64 = reader.result as string;
									onAvatarChange(croppedBase64);
								};
								reader.onerror = () => {
									ToastCommands.createToast({
										type: 'error',
										children: t`Failed to process the cropped image. Please try again.`,
									});
								};
								reader.readAsDataURL(croppedBlob);
							}}
							onSkip={() => {
								onAvatarChange(base64);
							}}
						/>
					)),
				);
			} catch {
				ToastCommands.createToast({
					type: 'error',
					children: t`That image is invalid. Please try another one.`,
				});
			}
		}, [hasAnimatedAvatar, onAvatarChange, t, i18n]);
		const handleModeChange = useCallback(
			(mode: AvatarMode) => {
				onAvatarModeChange?.(mode);
				if (mode === 'custom') {
					handleAvatarUpload();
				}
			},
			[onAvatarModeChange, handleAvatarUpload],
		);
		const avatarModeOptions = getAvatarModeOptions();
		const radioGroupDisabled = disableModeSelection ?? Boolean(disabled && !(isPerGuildProfile && !hasAnimatedAvatar));
		if (isPerGuildProfile && onAvatarModeChange) {
			return (
				<div>
					<div className={styles.label}>
						<Trans>Avatar</Trans>
					</div>
					<RadioGroup
						options={avatarModeOptions}
						value={avatarMode}
						disabled={radioGroupDisabled}
						onChange={handleModeChange}
						aria-label={t`Avatar mode selection`}
					/>
					{avatarMode === 'custom' && (
						<div className={styles.buttonGroup}>
							<Button variant="primary" small={true} onClick={handleAvatarUpload} disabled={disabled}>
								<Trans>Change Avatar</Trans>
							</Button>
							{hasAvatar && (
								<Button
									variant="secondary"
									small={true}
									onClick={() => {
										onAvatarModeChange('inherit');
										onAvatarClear();
									}}
									disabled={disabled}
								>
									<Trans>Remove Avatar</Trans>
								</Button>
							)}
						</div>
					)}
					<div className={clsx(styles.description, styles.helperSpacing)}>
						<Trans>
							{ANIMATED_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Recommended: {AVATAR_RECOMMENDED_SIZE_LABEL}
						</Trans>
					</div>
					{errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
				</div>
			);
		}
		return (
			<div>
				<div className={styles.label}>
					<Trans>Avatar</Trans>
				</div>
				<div className={styles.buttonGroup}>
					<Button variant="primary" small={true} onClick={handleAvatarUpload} disabled={disabled}>
						<Trans>Change Avatar</Trans>
					</Button>
					{hasAvatar && (
						<Button variant="secondary" small={true} onClick={onAvatarClear} disabled={disabled}>
							<Trans>Remove Avatar</Trans>
						</Button>
					)}
				</div>
				{!isPerGuildProfile && (
					<div className={styles.description}>
						{hasAnimatedAvatar ? (
							<Trans>
								{ANIMATED_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Recommended: {AVATAR_RECOMMENDED_SIZE_LABEL}
							</Trans>
						) : shouldShowPremiumFeatures() ? (
							<Trans>
								{STATIC_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Recommended: {AVATAR_RECOMMENDED_SIZE_LABEL}.
								Animated avatars (GIF) require <PlutoniumLink />.
							</Trans>
						) : (
							<Trans>
								{STATIC_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Recommended: {AVATAR_RECOMMENDED_SIZE_LABEL}.
							</Trans>
						)}
					</div>
				)}
				{isPerGuildProfile && (
					<div className={styles.description}>
						<Trans>
							{ANIMATED_IMAGE_FORMATS}. Max {IMAGE_MAX_SIZE_LABEL}. Recommended: {AVATAR_RECOMMENDED_SIZE_LABEL}
						</Trans>
					</div>
				)}
				{errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
			</div>
		);
	},
);
